/**
 * PasskeySetupHandoff — the held sign-in of an app that sets no passkey up (SECREQ S6d; owner ruling Q5, "Send them to
 * an app that sets one up").
 *
 * A tenant sign-in holds a person whose role requires a passkey they do not hold (403 `PASSKEY_SETUP_REQUIRED`, SECREQ
 * S4). An app that sets passkeys up enrols them in place. PortPuffin and ProspectPuffin set none up (owner ruling
 * Q-EE-34, "this app serves none"), so here the person is handed to the apps that do — the ONE generated set
 * (`@puffin-core/factor-enrollment-codes` `PASSKEY_SETUP_APP_KEYS`), never a hand-typed list — each a link to that
 * app's sign-in page for the person's company, in the two-factor handoff's shape (`<MfaEnrollmentHandoff>`, N152): it
 * says why, in the catalog's words, names this app and every one it links to by its brand name, and "Back" returns to
 * the sign-in form. It never signs anyone in.
 *
 * @module @puffin-core/login-shell/components/PasskeySetupHandoff
 */

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { PASSKEY_SETUP_APP_KEYS } from '@puffin-core/factor-enrollment-codes';
import { getBrand } from '@puffin-core/brand-assets';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';

/**
 * @param {Object} props
 * @param {string} props.appKey - THIS app's registry key: an app that sets no passkey up.
 * @param {(appKey: string) => string} props.loginUrlFor - Another app's sign-in page for the person's company.
 * @param {() => void} props.onBack - Return to the sign-in form.
 * @param {Function} [props.t] - The app's translator (see `useLoginTranslate`).
 * @returns {React.JSX.Element}
 * @throws {Error} For an app that sets a passkey up itself: it enrols in place, it never hands off.
 */
export function PasskeySetupHandoff({ appKey, loginUrlFor, onBack, t }) {
  const translate = useLoginTranslate(t);
  const firstLinkRef = useRef(null);
  // The step replaces the form the person just submitted: focus moves to its first forward action.
  useEffect(() => {
    firstLinkRef.current.focus();
  }, []);
  if (PASSKEY_SETUP_APP_KEYS.includes(appKey)) {
    throw new Error(
      `PasskeySetupHandoff: "${appKey}" sets a passkey up itself — it enrols in place, it never hands off`,
    );
  }

  return (
    <div data-testid="passkey-setup-handoff">
      <h2 className="mfa-challenge-title">{translate('login.passkeySetupHandoff.title')}</h2>
      <p className="mfa-challenge-subtitle" role="status">
        {translate('login.passkeySetupHandoff.body', { appName: getBrand(appKey).appName })}
      </p>
      <ul className="list-unstyled mb-md">
        {PASSKEY_SETUP_APP_KEYS.map((setupAppKey, index) => (
          <li key={setupAppKey} className="mb-sm">
            <a
              ref={index === 0 ? firstLinkRef : undefined}
              className="btn btn-primary"
              href={loginUrlFor(setupAppKey)}
            >
              {translate('login.passkeySetupHandoff.openApp', {
                appName: getBrand(setupAppKey).appName,
              })}
            </a>
          </li>
        ))}
      </ul>
      <div className="mfa-setup-actions">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          {translate('login.passkeySetupHandoff.back')}
        </button>
      </div>
    </div>
  );
}

PasskeySetupHandoff.propTypes = {
  appKey: PropTypes.string.isRequired,
  loginUrlFor: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  t: PropTypes.func,
};

export default PasskeySetupHandoff;
